import { cloneDeep, isNumber, mapValues, set } from "lodash";
import { BotAmountData } from "@shared/features/bots/types";
import {
  AmountType,
  ApiGeneralDataResponse,
  ApiTags,
  BacktestingRunType,
  BaseBotData,
  BaseGetBotGraphDataRequest,
  BasicBotData,
  BotConfigurations,
  BotData,
  BotGraphType,
  BotOptions,
  BotsGraphsDataResponse,
  BotStatus,
  BotTag,
  BotTemplateData,
  BotType,
  ConfigurationItem,
  Direction,
  DisplayCurrency,
  FiltersData,
  FilterType,
  FormattedBotsGraphsData,
  FormattedChartDataPoint,
  FormTrailingStopData,
  GetBacktestingRunsRequest,
  GetBotsDataRequest,
  GetBotsNamesRequest,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  NumberOption,
  OneClickFollowBotData,
  OrderBaseline,
  OrderTypes,
  PrecisionConfig,
  RawCustomStrategyData,
  RawLineChartDataPoint,
  SingleValueDataPoint,
  SortingOrder,
  Status,
  StrategyData,
  StrategyStyleType,
  Timeframe,
  UpdatedRawChartData,
} from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import {
  DISPLAY_CURRENCIES,
  LONG_SHORT_TO_DISPLAY,
  MARGIN_EXCHANGES,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants";
import {
  addUuidToConfigurationItems,
  convertValues,
  getAllConfigurationItems,
  getChildren,
  getItemByName,
  getItemValue,
  getName,
  getUuidToItems,
} from "@shared/features/configuration/utils";
import {
  getIsAllSelected,
  testNumberListFilter,
  testRangeFilter,
  testStringListFilter,
} from "@shared/features/filters/utils";
import { Color } from "@shared/common/Themes/types";
import {
  BOT_STATUS_TO_HIDE,
  BOT_TYPE_TO_DISPLAY_NAME,
  TRAILING_TOP_ITEM_NAME_TO_DATA_FIELD,
  USING_CHANNEL_SETTING,
} from "@shared/features/bots/constants";
import {
  BaseBotFormValues,
  ChannelValue,
  EntryGraceBaseline,
  LeverageType,
  MultiSymbolMarginData,
  MultiValue,
  OverrideType,
  StrategyBaseline,
  SymbolMarginData,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  ALL_OVERRIDES_TOGGLE_FIELDS,
  channelValue,
  DEFAULT_FIRST_ENTRY_TAKE_PROFIT,
  DEFAULT_SIGNALS_BOT_STRATEGY,
  DEFAULT_SIGNALS_CONFIGURATION_STRATEGY,
  DEFAULT_STOP,
  DEFAULT_TAKE_PROFIT,
  ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT,
  MARGIN_GLOBAL_SETTINGS_KEY,
  off,
  PRICE_ADJUSTMENT_BASELINE_TITLE,
  TOGGLE_FIELD_TO_OVERRIDE_KEY,
  WITHOUT_OFF_TOGGLE_FIELDS,
} from "@shared/features/bots/BotForm/common/constants";
import { isSymbol } from "@shared/types/utils";
import {
  CUSTOM_STRATEGIES_ITEM_NAME,
  DCA_STRATEGIES_ITEM_NAME,
} from "@shared/features/configuration/constants";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import { sharedLib } from "@shared/services/shared.service";
import { formatDate } from "@shared/utils/date";
import { TranslationKeys } from "@shared/common/i18n/types";
import { translateOptionName } from "@shared/common/i18n/optionNames";

type BotsTranslate = (
  key: TranslationKeys<LanguageNameSpaces.BOTS>,
  options?: Record<string, unknown>
) => string;

export const getIsPercentAmountType = (
  amountType: BaseBotFormValues["amountType"]
) => {
  return (
    !isSymbol(amountType) &&
    amountType &&
    [AmountType.RISK_PERCENTAGE, AmountType.PERCENTAGE].includes(amountType)
  );
};

export const getAmountDisplay = (
  amountType?: AmountType,
  amountValue?: number,
  showUserConfiguration?: boolean
) => {
  if (showUserConfiguration && !amountType)
    return translateOptionName(OverrideType.USER_CONFIGURATION);
  if (getIsPercentAmountType(amountType))
    amountValue = toFixed((amountValue || 0) * 100, 2);
  const suffix = getAmountTypeSuffix(amountType, true);
  return `${amountValue}${suffix}`;
};

export const getBotAmountData = (
  botData: Pick<BaseBotData, "options"> | BasicBotData
) => {
  const isUpdatedData =
    "amount_type" in botData &&
    "amount_value" in botData &&
    botData.amount_type &&
    botData.amount_value;
  const amountType = isUpdatedData
    ? botData.amount_type
    : (getOptionsItemValue(botData, "Amount Type") as AmountType);
  const amountValue = isUpdatedData
    ? botData.amount_value
    : getOptionsItemValue(botData, "Amount Value") || 0;
  return {
    amountType,
    amountValue,
  };
};

export const getBotAmountDisplay = (
  botData: Pick<BaseBotData, "options"> | BasicBotData,
  showUserConfiguration?: boolean
) => {
  const { amountType, amountValue } = getBotAmountData(botData);
  return getAmountDisplay(
    amountType,
    amountValue,
    showUserConfiguration ||
      ("bot_type" in botData &&
        botData.bot_type === BotType.TRADING_VIEW_SIGNALS)
  );
};

export const getOptionsItem = (
  botData: Pick<BaseBotData, "options"> | BasicBotData,
  optionsKey: keyof BotOptions
) => {
  return "options" in botData ? botData.options?.[optionsKey] : undefined;
};

export const getOptionsItemValue = (
  botData: Pick<BaseBotData, "options"> | BasicBotData,
  optionsKey: keyof BotOptions
) => {
  return getOptionsItem(botData, optionsKey)?.[1];
};

export const getAmountTypeSuffix = (
  amountType: AmountType | undefined | MultiValue,
  isLongRiskDisplay?: boolean
) => {
  let suffix: string = "";
  if (amountType === AmountType.FIXED_USD_AMOUNT) suffix = " USD";
  else if (amountType === AmountType.FIXED_BTC_AMOUNT) suffix = " BTC";
  else if (amountType === AmountType.PERCENTAGE) suffix = "%";
  else if (amountType === AmountType.RISK_PERCENTAGE)
    suffix = `%${isLongRiskDisplay ? " Risk" : ""}`;
  return suffix;
};

export const changeGraphDataBaseline = (
  graphData: RawLineChartDataPoint[],
  yValueIndex: number,
  totalChange: number
) => {
  const lastPointValue =
    (graphData.length > 0 && graphData[graphData.length - 1][yValueIndex]) || 0;
  const baselineValue = totalChange - lastPointValue;

  return graphData.map((dataPoint) => {
    const updatedDataPoint: RawLineChartDataPoint = [...dataPoint];
    updatedDataPoint[yValueIndex] += baselineValue;
    return updatedDataPoint;
  });
};

export const isGridPnlBotType = (botType: BotType) =>
  botType === BotType.GRID || botType === BotType.HEDGING;

export const changeBotsGraphDataBaseline = (
  graphData: RawLineChartDataPoint[],
  yValueIndex: number,
  filteredBots: BotData[] | BasicBotData[],
  displayCurrency: DisplayCurrency
) => {
  let totalChange = 0;
  filteredBots?.forEach((bot) => {
    if (isGridPnlBotType(bot.bot_type)) {
      totalChange += bot.realised_pnl?.[displayCurrency] || 0;
    } else {
      totalChange +=
        bot.change?.[displayCurrency === DISPLAY_CURRENCIES.BTC ? 1 : 0] || 0;
    }
  });
  return changeGraphDataBaseline(graphData, yValueIndex, totalChange);
};

export const getMyBotsStatisticsValues = (
  bots: BotData[] | BasicBotData[] | undefined,
  botType: BotType,
  displayCurrency: DisplayCurrency
): [number, number] => {
  const useGridPnl = isGridPnlBotType(botType);
  let numberOfActions = 0;
  let profit = 0;
  bots?.forEach((bot) => {
    numberOfActions +=
      (botType === BotType.HEDGING
        ? bot.number_of_rebalances
        : botType === BotType.GRID
        ? bot.take_profits_filled
        : bot.number_of_closed_trades) || 0;
    profit += useGridPnl
      ? bot.realised_pnl?.[displayCurrency] || 0
      : bot.change?.[displayCurrency === DISPLAY_CURRENCIES.BTC ? 1 : 0] || 0;
  });
  return [numberOfActions, profit];
};

export const filterBots = (
  botsData: BotData[] | BasicBotData[] | undefined,
  filtersData: FiltersData
) => {
  return botsData?.filter((botData) => {
    const botDirection = botData.reverse ? Direction.SHORT : Direction.LONG;
    const botStatus =
      botData.status === BotStatus.ACTIVE
        ? Status.ACTIVE
        : botData.status === BotStatus.ARCHIVED
        ? Status.ARCHIVED
        : Status.INACTIVE;
    const isTradingViewSignalsBot =
      botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
    const symbolsFilterData = filtersData[FilterType.SYMBOLS];
    const validSymbols =
      botData.bot_type === BotType.SIGNAL ||
      !symbolsFilterData ||
      getIsAllSelected(FilterType.SYMBOLS, symbolsFilterData) ||
      (!!botData.symbols &&
        testStringListFilter(symbolsFilterData?.value, botData.symbols?.[0]));
    const validAccounts =
      isTradingViewSignalsBot ||
      testStringListFilter(
        filtersData[FilterType.CLIENTS]?.value,
        botData.client_name
      );
    const validExchanges =
      !isTradingViewSignalsBot ||
      testStringListFilter(
        filtersData[FilterType.EXCHANGES]?.value,
        botData.exchanges
      );
    return (
      validSymbols &&
      validAccounts &&
      validExchanges &&
      testStringListFilter(
        filtersData[FilterType.LONG_SHORT]?.value,
        botDirection
      ) &&
      testStringListFilter(
        filtersData[FilterType.MARGIN_TYPE]?.value,
        MARGIN_TYPE_TO_DISPLAY[botData.margin_type || MarginTypes.ISOLATED]
      ) &&
      testRangeFilter(
        filtersData[FilterType.LEVERAGE]?.value,
        botData.leverage || 1
      ) &&
      testStringListFilter(filtersData[FilterType.STATUS]?.value, botStatus) &&
      testRangeFilter(
        filtersData[FilterType.TIME_FRAME]?.value?.range,
        botData.created_at
      ) &&
      (botData.bot_type !== BotType.SIGNAL ||
        testNumberListFilter(
          filtersData[FilterType.MULTI_GROUPS]?.value,
          botData.sub_group_id
        ))
    );
  }) as BotData[] | BasicBotData[] | undefined;
};

export const getFormattedBotsGraphsData = (
  bots: BasicBotData[] | BotData[] | undefined,
  displayCurrency: DisplayCurrency,
  precisionConfig: PrecisionConfig | undefined,
  botsGraphsData: BotsGraphsDataResponse | undefined
): FormattedBotsGraphsData => {
  const timestamps = botsGraphsData?.timestamps;
  if (
    !botsGraphsData ||
    !precisionConfig ||
    !bots ||
    bots.length === 0 ||
    !timestamps
  ) {
    return {
      accumulatedProfit: [],
      profitByDay: [],
    };
  }

  const precision =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? precisionConfig.btc_amount
      : precisionConfig.usd_amount;
  const filteredBotsIds = new Set(bots.map((botData) => botData.id));

  const getFormattedGraphData = (
    graphType: "accumulated_profit" | "profit_by_day"
  ) => {
    const botIdToPrevValue: Record<number, number> = {};
    const botsData = botsGraphsData[graphType]?.[displayCurrency] || {};
    let graphData = timestamps.reduce((acc, timestamp, index) => {
      const dataPoint: SingleValueDataPoint = [timestamp, 0];
      Object.entries(botsData).forEach(([botId, botGraphData]) => {
        const numberBotId = parseInt(botId);
        if (!filteredBotsIds.has(numberBotId)) return;
        let value: number;
        if (botGraphData && index.toString() in botGraphData) {
          value = botGraphData[index.toString()] || 0;
          botIdToPrevValue[numberBotId] = value;
        } else {
          value = botIdToPrevValue[numberBotId] || 0;
        }
        dataPoint[1] += value;
      });
      acc.push(dataPoint);
      return acc;
    }, [] as RawLineChartDataPoint[]);

    if (graphType === "accumulated_profit") {
      graphData = changeBotsGraphDataBaseline(
        graphData,
        1,
        bots,
        displayCurrency
      );
    }

    return formatTimeSeriesChartData(
      graphData,
      1,
      false,
      undefined,
      precision || 0
    );
  };

  return {
    accumulatedProfit: getFormattedGraphData("accumulated_profit"),
    profitByDay: getFormattedGraphData("profit_by_day"),
  };
};

export const getBotMarginDisplay = (
  botData: BaseBotData,
  t?: BotsTranslate
) => {
  const leverage = botData.leverage;
  const marginType = botData.margin_type;

  if (botData.exchange === "FTX Futures")
    return t?.("summary_values.from_configuration");
  else if (leverage && marginType) {
    return `${translateOptionName(
      MARGIN_TYPE_TO_DISPLAY[marginType]
    )} ${leverage}x`;
  }
  return null;
};

export const getBotLongShortDisplay = (
  botData: BaseBotData | BasicBotData,
  spotDisplay = translateOptionName("Spot")
) => {
  const exchange = botData.exchange ?? botData.exchanges?.[0];
  if (!MARGIN_EXCHANGES.has(exchange)) return spotDisplay;
  let longShort: LongShort[];
  if (botData.reverse === null || botData.reverse === undefined) {
    longShort = [LongShort.LONG, LongShort.SHORT];
  } else if (botData.reverse) {
    longShort = [LongShort.SHORT];
  } else {
    longShort = [LongShort.LONG];
  }
  return longShort
    .map((direction) => translateOptionName(LONG_SHORT_TO_DISPLAY[direction]))
    .join(", ");
};

export const getOverrideValue = (
  bot: BotData | BaseBotData,
  toggleField: ToggleField,
  fieldValue: any,
  isChannelConfiguration?: boolean
) => {
  if (bot.bot_type !== BotType.SIGNAL || isChannelConfiguration)
    return fieldValue;
  if (
    ALL_OVERRIDES_TOGGLE_FIELDS.has(toggleField) ||
    WITHOUT_OFF_TOGGLE_FIELDS.has(toggleField)
  ) {
    const overrideKey = TOGGLE_FIELD_TO_OVERRIDE_KEY[toggleField];
    const shouldOverride = !!overrideKey && bot[overrideKey];
    if (fieldValue === off) return undefined;
    if (!!overrideKey && !shouldOverride && !fieldValue) return channelValue;
  }
  return fieldValue;
};

export const getBotTrailingOrderDisplay = (
  botData: BaseBotData,
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT,
  isChannelConfiguration?: boolean,
  showUserConfiguration?: boolean,
  t?: BotsTranslate
) => {
  if (botData.bot_type === BotType.HEDGING) return;
  const isEntry = orderType === OrderTypes.ENTRY;
  const trailingValue = getConfigurationItemValue(
    botData,
    isEntry ? "Trailing Entry" : "Trailing Take-Profit"
  );
  const parsedValue = getOverrideValue(
    botData,
    isEntry ? ToggleField.TRAILING_ENTRY : ToggleField.TRAILING_TAKE_PROFIT,
    trailingValue,
    isChannelConfiguration
  );
  if (!parsedValue) {
    if (showUserConfiguration)
      return translateOptionName(OverrideType.USER_CONFIGURATION);
    return isChannelConfiguration ? undefined : translateOptionName("Off");
  } else if (typeof parsedValue === "number") {
    return `${parsedValue}%`;
  } else if (parsedValue === channelValue) {
    return t?.(USING_CHANNEL_SETTING);
  }
};

export const getFirstEntryGraceDisplay = (
  botData: BaseBotData,
  hideMissingFields?: boolean,
  t?: BotsTranslate
) => {
  const optionsKey = "Entry Grace Percentage";
  const isMissingField =
    hideMissingFields && !(optionsKey in (botData.options || {}));
  if (botData.bot_type !== BotType.SIGNAL || isMissingField) return null;
  const percent = getOptionsItemValue(botData, optionsKey);
  const baseline = botData.entry_grace_baseline;
  const targetExchange = botData.entry_grace_target_exchange;

  if (!percent) {
    return {
      percent: translateOptionName("Off"),
      baseline: undefined,
      targetExchange,
    };
  } else if (typeof percent === "number") {
    return {
      percent: t?.("summary_values.maximum_price_cap", {
        value: toFixed(percent * 100, 2),
      }),
      baseline: t?.("summary_values.when_to_activate", {
        value: t?.(
          ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT[
            baseline ?? EntryGraceBaseline.PRICE_REACHED
          ]
        ),
      }),
      targetExchange,
    };
  }
};

export const getTakeProfitGraceDisplay = (
  botData: BaseBotData,
  hideMissingFields?: boolean
) => {
  const optionsKey = "Take Profit Grace Percentage";
  const isMissingField =
    hideMissingFields && !(optionsKey in (botData.options || {}));
  if (botData.bot_type !== BotType.SIGNAL || isMissingField) return null;
  const percent = getOptionsItemValue(botData, optionsKey);
  const targetExchange = botData.take_profit_grace_target_exchange;

  if (!percent) {
    return { percent: translateOptionName("Off"), targetExchange };
  }

  return { percent: `${toFixed(percent * 100, 2)}%`, targetExchange };
};

export const getCancelOnProfitBeforeEntryDisplay = (
  botData: BaseBotData,
  hideMissingFields?: boolean,
  isAdminConfiguration?: boolean,
  isChannelConfiguration?: boolean,
  t?: BotsTranslate
) => {
  if (hideMissingFields && !("cancel_on_profit_before_entry" in botData))
    return;
  const cancelOnProfitBeforeEntry = botData.cancel_on_profit_before_entry;
  if (
    botData.bot_type === BotType.SIGNAL ||
    isAdminConfiguration ||
    isChannelConfiguration
  ) {
    if (typeof cancelOnProfitBeforeEntry !== "boolean")
      return isAdminConfiguration || isChannelConfiguration
        ? translateOptionName("Dynamic")
        : t?.(USING_CHANNEL_SETTING);
    return translateOptionName(cancelOnProfitBeforeEntry ? "On" : "Off");
  }
};

export const getBotDefaultOrderDisplay = (
  botData: BaseBotData,
  orderType: OrderTypes.STOP | OrderTypes.TAKE_PROFIT,
  hideMissingFields?: boolean,
  t?: BotsTranslate
) => {
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isTradingViewSignalsBot =
    botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const isStop = orderType === OrderTypes.STOP;
  const name = isStop ? "Stop" : "Take-Profit";
  const percentKey = `Default ${name} Percentage` as const;
  const percent = getOptionsItemValue(botData, percentKey);
  const baselineKey = `Default ${name} Baseline` as const;
  const baseline = getOptionsItemValue(botData, baselineKey);
  const baselineOptions = botData?.options?.[baselineKey]?.[0];
  const botOptions = botData?.options || {};

  const isMissingField =
    !(baselineKey in botOptions) && !(percentKey in botOptions);
  if (hideMissingFields && isMissingField) return;

  const isBaselineOff =
    !baseline || baseline === OrderBaseline.ORIGINAL_FIRST_ENTRY;
  const baselineDisplay = isBaselineOff
    ? undefined
    : baselineOptions?.find((option) => option.value === baseline)?.name;
  const valueExists = percent || baseline;
  const overrideValue = getOverrideValue(
    botData,
    isStop ? ToggleField.STOP : ToggleField.TAKE_PROFITS,
    baseline
  );

  if ((isSignalsBot && !isStop) || valueExists) {
    const percentDisplay = percent ? toFixed(percent * 100, 2) : undefined;
    let baseline: string | undefined;
    if (isBaselineOff) baseline = translateOptionName("Off");
    else if (isSignalsBot && orderType === OrderTypes.TAKE_PROFIT)
      baseline = `${t?.(PRICE_ADJUSTMENT_BASELINE_TITLE)}: ${baselineDisplay}`;
    else baseline = t?.("summary_values.baseline", { value: baselineDisplay });
    return {
      distance: percentDisplay
        ? t?.("summary_values.distance", { value: percentDisplay })
        : isSignalsBot
        ? t?.(USING_CHANNEL_SETTING)
        : translateOptionName("Off"),
      baseline,
    };
  } else if (isStop && overrideValue === channelValue) {
    return t?.(USING_CHANNEL_SETTING);
  } else if (!overrideValue && isStop) {
    return translateOptionName(
      isTradingViewSignalsBot ? OverrideType.USER_CONFIGURATION : "Off"
    );
  }
};

export const getBotAvailableAmountDisplay = (
  botAmountData: BotAmountData | undefined,
  displayCurrency: DisplayCurrency
) => {
  const availableMainCoinAmount = toFixed(
    botAmountData?.mainCoinAvailableAmount || 0,
    botAmountData?.mainCoinAmountPrecision
  );
  let usdAmount: number | undefined;
  if (botAmountData && (botAmountData?.mainCoin || "") !== "USD") {
    usdAmount = botAmountData.convertMainCoinAmountToDisplayCurrency(
      botAmountData?.mainCoinAvailableAmount || 0,
      displayCurrency,
      true
    );
  }

  return `${availableMainCoinAmount} ${botAmountData?.mainCoin || ""}${
    usdAmount !== undefined ? ` (${usdAmount} ${displayCurrency})` : ""
  }`;
};

export const convertTargetsToPercentages = (targets?: number[]) =>
  targets?.map((target) => toFixed(target * 100, 2));

export const getStrategy = (
  botData: BaseBotData,
  orderType: OrderTypes,
  botType: BotType,
  isChannelConfiguration?: boolean
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const isEntry = orderType === OrderTypes.ENTRY;
  const strategyFieldName = isEntry ? "buying_strategy" : "selling_strategy";
  const strategy = isSignalsBot
    ? botData[strategyFieldName]
    : getOverrideValue(
        botData,
        isEntry ? ToggleField.ENTRIES : ToggleField.TAKE_PROFIT_STRATEGY,
        botData[strategyFieldName] || undefined,
        isChannelConfiguration
      );
  const isValid = !isSymbol(strategy) && strategy;
  const isCustom = isValid && "targets" in strategy;
  const isBuiltIn = isValid && "type" in strategy;
  let parsedStrategy: StrategyData | undefined | ChannelValue;
  if (isSymbol(strategy)) {
    parsedStrategy = channelValue;
  } else if (!strategy && isSignalsBot) {
    parsedStrategy = isChannelConfiguration
      ? DEFAULT_SIGNALS_CONFIGURATION_STRATEGY
      : DEFAULT_SIGNALS_BOT_STRATEGY;
  } else if (strategy) {
    const priceDifference =
      strategy.price_difference && strategy.price_difference * 100;
    const numberOfActiveOrders = strategy.number_of_active_orders ?? undefined;
    const baseline =
      strategy.baseline ??
      (isChannelConfiguration ? undefined : StrategyBaseline.PREVIOUS_ORDER);
    const baseStrategyData = {
      price_scale: strategy.price_scale,
      price_difference: priceDifference,
      number_of_active_orders: numberOfActiveOrders,
      number_of_orders: strategy.number_of_orders,
      baseline,
    };
    if (isBuiltIn) {
      parsedStrategy = {
        ...baseStrategyData,
        strategy_style: StrategyStyleType.BUILT_IN,
        builtin_strategy_type: strategy.type,
        name: strategy.name,
      };
    } else if (isCustom) {
      const customStrategy = { ...strategy } as Omit<
        RawCustomStrategyData,
        "strategy_style"
      >;
      parsedStrategy = {
        ...baseStrategyData,
        targets: convertTargetsToPercentages(customStrategy.targets),
        strategy_style: StrategyStyleType.CUSTOM,
      };
      if (customStrategy.targets && !isSymbol(customStrategy.targets)) {
        parsedStrategy.number_of_orders = findLastNonZeroValue(
          customStrategy.targets
        );
      }
    } else {
      const firstOrderPercentage =
        strategy.first_order_percentage &&
        toFixed(strategy.first_order_percentage * 100, 2);
      parsedStrategy = {
        ...baseStrategyData,
        amount_scale: strategy.amount_scale,
        first_order_percentage: firstOrderPercentage ?? undefined,
        strategy_style: StrategyStyleType.DCA,
      };
    }
  }
  if (
    isSignalsBot &&
    !isChannelConfiguration &&
    parsedStrategy &&
    !isSymbol(parsedStrategy)
  ) {
    const strategyKeys = new Set([
      "price_scale",
      "price_difference",
      "number_of_active_orders",
      "number_of_orders",
      "targets",
      "amount_scale",
      "builtin_strategy_type",
      "strategy_style",
    ]);
    const strategy = mapValues(parsedStrategy, (value, key) => {
      if (strategyKeys.has(key) && !value) return channelValue;
      return value;
    }) as StrategyData | ChannelValue | undefined;
    if (strategy && !isSymbol(strategy) && isSymbol(strategy?.price_scale)) {
      strategy.baseline = channelValue;
    }
    return strategy;
  }
  return parsedStrategy;
};

export const getConfigurationItem = (
  botData: { configurations?: BotConfigurations } | undefined,
  configurationKey: keyof BotConfigurations
): ConfigurationItem | undefined => {
  return botData?.configurations?.[configurationKey]?.data?.[0];
};

export const getBotTemplateStrategies = (
  botData: { configurations?: BotConfigurations } | undefined,
  orderType: OrderTypes
): ConfigurationItem[] => {
  const strategiesItem = getConfigurationItem(
    botData,
    orderType === OrderTypes.ENTRY ? "Entry Strategy" : "Take-Profit Strategy"
  );
  return getChildren(strategiesItem).filter(
    (item: ConfigurationItem) =>
      ![DCA_STRATEGIES_ITEM_NAME, CUSTOM_STRATEGIES_ITEM_NAME].includes(
        getName(item)
      )
  );
};

export const getConfigurationItemValue = (
  botData: BaseBotData,
  configurationKey: keyof BotConfigurations
) => {
  return getItemValue(getConfigurationItem(botData, configurationKey));
};

export const getBotTrailingStopDisplay = (
  botData: BaseBotData,
  isChannelConfiguration: boolean | undefined,
  showUserConfiguration: boolean | undefined,
  t: BotsTranslate
) => {
  const trailingStop = botData.trailing_stop;
  const { trigger, percent, type } = trailingStop || {};
  const parsedTrigger = getOverrideValue(
    botData,
    ToggleField.TRAILING_STOP,
    trigger,
    isChannelConfiguration
  );
  // @ts-ignore
  const valueExists = trigger && type && type !== 0; // TODO: type changes to enum from backend
  const isOffValue = trigger === off || percent === off || type === off;
  if (valueExists && !isOffValue) {
    const triggerType = trigger.type;
    let triggerValue = trigger.value;
    if (triggerType === "Percent") {
      triggerValue = toFixed(triggerValue * 100, 2);
    }
    const triggerDisplay =
      triggerType === "Target"
        ? t("summary_values.target_number", { value: triggerValue })
        : `${triggerValue}${triggerType === "Percent" ? "%" : ""}`;
    const trailingStopPercent = isNumber(percent)
      ? toFixed(percent * 100, 2)
      : undefined;
    return {
      type: t("summary_values.trailing_type", { value: type }),
      percent:
        trailingStopPercent !== undefined
          ? t("summary_values.trailing_percent", {
              value: trailingStopPercent,
            })
          : undefined,
      trigger: t("summary_values.trigger", { value: triggerDisplay }),
    };
  }
  if (isChannelConfiguration && !trailingStop) {
    return;
  } else if (!parsedTrigger) {
    return translateOptionName(
      showUserConfiguration ? OverrideType.USER_CONFIGURATION : "Off"
    );
  } else if (
    botData.bot_type === BotType.SIGNAL &&
    parsedTrigger === channelValue
  ) {
    return t(USING_CHANNEL_SETTING);
  }
};

export const getFormattedBotGraphData = (
  botGraphData: ApiGeneralDataResponse<UpdatedRawChartData> | undefined,
  args: BaseGetBotGraphDataRequest,
  displayCurrency: DisplayCurrency,
  botData?: BotData,
  totalChangePercent?: number
): FormattedChartDataPoint[] => {
  const { data, precision: precisionConfig } = botGraphData || {};
  if (!data || !precisionConfig) return [];

  let graphData: RawLineChartDataPoint[] = (data[args.time_range] || []).map(
    (dataPoint) => {
      if (typeof dataPoint[1] === "number")
        return dataPoint as RawLineChartDataPoint;
      return [dataPoint[0], ...dataPoint[1]];
    }
  );

  const isPercentGraph = [
    BotGraphType.PROFIT_BY_DAY_PERCENTAGE,
    BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE,
  ].includes(args.graph_type);
  let precision: number | undefined;
  if (isPercentGraph) precision = precisionConfig.general_percent || 2;
  else if (displayCurrency === DISPLAY_CURRENCIES.BTC)
    precision = precisionConfig.btc_amount;
  else precision = precisionConfig.usd_amount;

  const yValueIndex =
    displayCurrency === DISPLAY_CURRENCIES.BTC && !isPercentGraph ? 2 : 1;

  if (args.graph_type === BotGraphType.ACCUMULATING_PROFIT && botData) {
    graphData = changeBotsGraphDataBaseline(
      graphData,
      yValueIndex,
      [botData],
      displayCurrency
    );
  } else if (
    args.graph_type === BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE &&
    totalChangePercent !== undefined
  ) {
    graphData = changeGraphDataBaseline(
      graphData,
      yValueIndex,
      totalChangePercent
    );
  }

  return formatTimeSeriesChartData(
    graphData,
    yValueIndex,
    false,
    isPercentGraph ? "%" : undefined,
    precision || 0
  );
};

export const getBotDeleteWarningMessage = (isBulk: boolean, t: BotsTranslate) =>
  `${t(
    isBulk
      ? "messages.warnings.confirm_bulk_delete_bots"
      : "messages.warnings.confirm_delete"
  )}\n${t(
    isBulk
      ? "messages.warnings.bulk_open_trades_remain"
      : "messages.warnings.open_trades_remain"
  )}`;

export const getBotDeletedMessage = (
  botType: BotType,
  isBulk: boolean,
  t: BotsTranslate
) =>
  t(
    isBulk
      ? "messages.success.bots_deleted_with_type"
      : "messages.success.bot_deleted_with_type",
    { botType: t(BOT_TYPE_TO_DISPLAY_NAME[botType]) }
  );

export const getBotDeleteFailedMessage = (
  botType: BotType,
  isBulk: boolean,
  t: BotsTranslate
) =>
  t(
    isBulk
      ? "messages.errors.bulk_delete_failed_with_type"
      : "messages.errors.delete_failed_with_type",
    { botType: t(BOT_TYPE_TO_DISPLAY_NAME[botType]) }
  );

export const getDefaultOrder = (
  botData: BaseBotData,
  orderType: OrderTypes.STOP | OrderTypes.TAKE_PROFIT,
  useDefaultValue?: boolean
): [BaseBotFormValues["defaultStop"], boolean, boolean] => {
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isTradingViewSignalsBot =
    botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const isStop = orderType === OrderTypes.STOP;
  const name = isStop ? "Stop" : "Take-Profit";
  let percent = getOptionsItemValue(botData, `Default ${name} Percentage`);
  let baseline = getOptionsItemValue(botData, `Default ${name} Baseline`);
  const isOverride = isStop
    ? botData.override_default_stop
    : botData.override_default_take_profit;

  const isSignalsBotTakeProfit = isSignalsBot && !isStop;
  let isDefaultOrderOn = false;
  let isDynamicTakeProfitOn = false;
  let defaultOrder: BaseBotFormValues["defaultStop"] = undefined;
  if (useDefaultValue) {
    defaultOrder = isStop
      ? DEFAULT_STOP
      : isTradingViewSignalsBot
      ? DEFAULT_FIRST_ENTRY_TAKE_PROFIT
      : DEFAULT_TAKE_PROFIT;
  }
  const valueExists = !!percent || !!baseline;
  if (isSignalsBotTakeProfit || valueExists) {
    if (baseline && baseline !== OrderBaseline.ORIGINAL_FIRST_ENTRY) {
      isDynamicTakeProfitOn = true;
    } else {
      baseline = OrderBaseline.AVERAGE_ENTRIES;
    }
    let parsedPercent: string | ChannelValue | undefined = undefined;
    if (isSignalsBotTakeProfit || percent !== undefined) {
      parsedPercent =
        percent !== undefined && percent !== null
          ? toFixed((percent || 0) * 100, 2).toString()
          : channelValue;
    }
    if (parsedPercent) {
      isDefaultOrderOn = true;
    }
    defaultOrder = {
      percent: parsedPercent,
      baseline,
    };
  } else if (isStop && isSignalsBot && !valueExists) {
    defaultOrder = isOverride ? undefined : channelValue;
    isDefaultOrderOn = defaultOrder === channelValue;
  }
  return [defaultOrder, isDefaultOrderOn, isDynamicTakeProfitOn];
};

export const getBotMultiSymbolMarginData = (
  botData: BaseBotData
): [BaseBotFormValues["multiSymbolMarginData"], boolean] => {
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  let isOn = false;
  const rawData = botData.symbol_to_leverage_data;
  let multiSymbolMarginData: BaseBotFormValues["multiSymbolMarginData"] =
    undefined;
  if (isSignalsBot && !rawData) {
    isOn = true;
    multiSymbolMarginData = channelValue;
  } else if (rawData) {
    isOn = true;
    multiSymbolMarginData = mapValues(rawData, (rawSymbolMarginData) => {
      const [marginType, multiplier, leverageType] = rawSymbolMarginData;
      return {
        marginType: marginType ?? channelValue,
        multiplier: multiplier ?? channelValue,
        leverageType: multiplier
          ? leverageType ?? LeverageType.EXACT
          : channelValue,
      };
    }) as BaseBotFormValues["multiSymbolMarginData"];
  }
  return [multiSymbolMarginData, isOn];
};

export const getBotTagsData = (
  botData: BotTemplateData
): { tag: BotTag; color: Color }[] => {
  const botTagToColor: Record<BotTag, Color> = {
    [BotTag.NEW]: "green",
    [BotTag.POPULAR]: "gold",
    [BotTag.TRENDING]: "purple",
  } as const;

  const tagsData: { tag: BotTag; color: Color }[] = [];
  Object.entries(botData.bot_tags || {}).forEach(([tag, isPresent]) => {
    const color = botTagToColor[tag as BotTag] || "primaryBlue";
    if (isPresent && color) {
      tagsData.push({
        tag: (tag.charAt(0).toUpperCase() + tag.slice(1)) as BotTag,
        color: botTagToColor[tag as BotTag],
      });
    }
  });
  return tagsData;
};

export const getOptionNameByValue = (
  optionValue: number | null | undefined,
  array: NumberOption[]
) => {
  return array.find((option) => option.value === optionValue)?.name;
};

export const getOptionValueByName = (
  optionName: string | undefined,
  array: NumberOption[]
) => {
  return array.find((option) => option?.name === optionName)?.value;
};

export const getDirectionAndMarginTag = (botData?: {
  exchange?: string;
  reverse?: boolean;
  margin_type?: MarginTypes;
  leverage?: number;
}) => {
  const isMarginExchange =
    !!botData && !!botData.exchange && MARGIN_EXCHANGES.has(botData.exchange);
  if (!isMarginExchange) return translateOptionName("Spot");
  let tagText = translateOptionName(botData?.reverse ? "Short" : "Long");
  if (botData?.margin_type)
    tagText = tagText.concat(
      " - ",
      translateOptionName(MARGIN_TYPE_TO_DISPLAY[botData.margin_type]),
      ` ${botData.leverage}x`
    );
  return tagText;
};

export const getParsedTrailingStopFromConfiguration = (
  configuration?: ConfigurationItem
) => {
  const trailingStopData = {};
  Object.entries(TRAILING_TOP_ITEM_NAME_TO_DATA_FIELD).forEach(
    ([itemName, dataFieldName]) => {
      const item = getItemByName(itemName, configuration);
      if (item) {
        set(trailingStopData, dataFieldName, item.value);
      }
    }
  );
  return trailingStopData as FormTrailingStopData;
};

export const getBotParsedTrailingStop = (bot: BaseBotData) => {
  const configuration = cloneDeep(getConfigurationItem(bot, "Trailing Stop"));
  addUuidToConfigurationItems(configuration);
  const uuidToItem = getUuidToItems(configuration);
  const idToItem = getAllConfigurationItems(configuration);
  convertValues(configuration, cloneDeep(idToItem), Object.values(uuidToItem));
  return getParsedTrailingStopFromConfiguration(configuration);
};

export const getEditBotsInvalidateTags = (botsIds: number[]) => {
  return [
    { type: ApiTags.BOTS, id: "LIST" },
    { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
    ...botsIds.map((botId) => ({ type: ApiTags.BOTS, id: botId })),
  ];
};

export const sortBotsByAmount = (
  a: BotData | BasicBotData,
  b: BotData | BasicBotData,
  sortDirection: SortingOrder
) => {
  const aBotData = getBotAmountData(a);
  const bBotData = getBotAmountData(b);
  return (
    (bBotData.amountType || 0) - (aBotData.amountType || 0) ||
    sortDirection * ((aBotData.amountValue || 0) - (bBotData.amountValue || 0))
  );
};

export const getBotsInactiveAccountMessage = (
  actionName: string,
  isInvalid = true,
  isInactive?: boolean
) => {
  let text = "invalid";
  if (isInvalid && isInactive) text = "invalid or inactive";
  else if (isInactive) text = "inactive";
  return `You can't ${actionName} bots that are linked to an ${text} account.`;
};

export const getBotsInvalidGroupMessage = (actionName: string) => {
  return `This bot cannot be ${actionName} because it is connected to a group you are no longer a member of.`;
};

const updateBotsCache = (
  bots: ApiGeneralDataResponse<BasicBotData[]>,
  updatedBot: BasicBotData,
  includeArchived: boolean
) => {
  if (bots) {
    const botsData = bots.data;
    if (!botsData) return;
    const botIndex = botsData.findIndex(
      (bot: BasicBotData) => bot.id === updatedBot.id
    );
    const isArchived = updatedBot.status === BotStatus.ARCHIVED;
    const shouldHide =
      BOT_STATUS_TO_HIDE.includes(updatedBot.status) ||
      (!includeArchived && isArchived);

    if (!shouldHide) {
      if (botIndex === -1) botsData.push(updatedBot);
      else botsData[botIndex] = updatedBot;
    } else if (botIndex >= 0) {
      botsData.splice(botIndex, 1);
    }
  }
};

export const updateBotsData = (
  updatedBots: BasicBotData[],
  botType: BotType
) => {
  return (dispatch: any) => {
    [true, false].forEach((includeArchived) => {
      const args: GetBotsDataRequest = {
        bot_type: botType,
        include_archived: includeArchived,
      };
      dispatch(
        sharedLib.sharedApi.bots.util.updateQueryData(
          "getBotsData",
          args,
          (bots) => {
            updatedBots.forEach((updatedBot) =>
              updateBotsCache(
                bots as ApiGeneralDataResponse<BasicBotData[]>,
                updatedBot,
                includeArchived
              )
            );
          }
        )
      );
    });
  };
};

export const getBotOneClickFollowCacheKey = (
  bot: BasicBotData | OneClickFollowBotData
) => `oneClickDefault-${bot.id}`;

export const getPublicBotErrorTitle = (
  t: (key: TranslationKeys<LanguageNameSpaces.BOTS>) => string,
  errors?: {
    isInvalidLink: boolean;
    isPrivate: boolean;
    generalError: boolean;
  }
) => {
  if (errors?.isInvalidLink) return t("public_bots.invalid_bot_link");
  if (errors?.isPrivate) return t("public_bots.bot_not_valid");
  if (errors?.generalError) return t("public_bots.something_went_wrong");
};

export const getPublicBotErrorMessage = (
  t: (key: TranslationKeys<LanguageNameSpaces.BOTS>) => string,
  errors?: {
    isInvalidLink: boolean;
    isPrivate: boolean;
    generalError: boolean;
  }
) => {
  if (errors?.isInvalidLink) return t("public_bots.invalid_message");
  if (errors?.isPrivate) return t("public_bots.private_message");
  if (errors?.generalError) return t("public_bots.general_error");
};

export const getDisplayedTimeframe = (timeframe?: Timeframe) => {
  return `${formatDate(timeframe?.start, false, true)} - ${formatDate(
    timeframe?.end,
    false,
    true
  )}`;
};

export const updateGetBacktestingRuns = (
  newBacktestingRun: BacktestingRunType,
  args?: GetBacktestingRunsRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBacktestingRuns",
    args,
    (BacktestingRunsData) => {
      BacktestingRunsData?.data.unshift(newBacktestingRun);
    }
  );
};

export const removeBacktestingFromList = (
  backtestingRunId: number,
  args?: GetBacktestingRunsRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBacktestingRuns",
    args,
    (BacktestingRunsData) => {
      const index = BacktestingRunsData.data.findIndex(
        (run) => run.id === backtestingRunId
      );
      BacktestingRunsData.data.splice(index, 1);
    }
  );
};

export const updateGetBotsNames = (
  botId: number,
  botName: string,
  args: GetBotsNamesRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBotsNames",
    args,
    (botsNames) => {
      Object.assign(botsNames || {}, { [botId]: botName });
    }
  );
};

export const removeBotNameFromList = (
  botId: number,
  args: GetBotsNamesRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBotsNames",
    args,
    (botsNames) => {
      delete botsNames?.[botId];
    }
  );
};

const appendIndexToName = (name?: string, index?: number) => {
  return `${name}${index ? ` #${index}` : ""}`;
};

export const makeNameUnique = (
  name: string,
  botsNames?: Record<number, string>
) => {
  const strIndexMatch = name.match(new RegExp(/\s#(\d+)$/))?.[1];
  let index = strIndexMatch ? Number(strIndexMatch) : 1;
  const strippedName = name.replace(new RegExp(/\s#\d+$/), "");
  let defaultName = name;
  const names = new Set(Object.values(botsNames || {}));
  while (names.has(defaultName)) {
    defaultName = appendIndexToName(strippedName, index);
    index += 1;
  }
  return defaultName;
};

export const getDefaultMarginData = (): SymbolMarginData => ({
  marginType: channelValue,
  multiplier: channelValue,
  leverageType: channelValue,
});

export const getDefaultGlobalSettings = (): MultiSymbolMarginData => {
  return { [MARGIN_GLOBAL_SETTINGS_KEY]: getDefaultMarginData() };
};

export const getHasCustomSymbols = (data: MultiSymbolMarginData) => {
  return Object.keys(data).some(
    (symbol) => symbol !== MARGIN_GLOBAL_SETTINGS_KEY
  );
};

export const formatTimeoutDisplay = (
  numSeconds: number | undefined,
  t: (
    key: TranslationKeys<LanguageNameSpaces.COMMON>,
    options?: Record<string, any>
  ) => string
) => {
  if (!numSeconds) return t("time.zero");
  const SECONDS_IN_MINUTE = 60;
  const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
  const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;

  const days = Math.floor(numSeconds / SECONDS_IN_DAY);
  const remainingAfterDays = numSeconds % SECONDS_IN_DAY;
  const hours = Math.floor(remainingAfterDays / SECONDS_IN_HOUR);
  const remainingAfterHours = remainingAfterDays % SECONDS_IN_HOUR;
  const minutes = Math.floor(remainingAfterHours / SECONDS_IN_MINUTE);
  const seconds = remainingAfterHours % SECONDS_IN_MINUTE;

  const parts: string[] = [];
  if (days)
    parts.push(t(days === 1 ? "time.day" : "time.day_plural", { count: days }));
  if (hours)
    parts.push(
      t(hours === 1 ? "time.hour" : "time.hour_plural", { count: hours })
    );
  if (minutes)
    parts.push(
      t(minutes === 1 ? "time.minute" : "time.minute_plural", {
        count: minutes,
      })
    );
  if (seconds || parts.length === 0)
    parts.push(
      t(seconds === 1 ? "time.second" : "time.second_plural", {
        count: seconds,
      })
    );

  return parts.filter(Boolean).join(", ");
};
